'use client';

import { ArrowLeft, Download, Share2, ExternalLink, Monitor } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { useState } from 'react';
import { CriterionTag } from '@/components/audit/CriterionTag';
import { BreakEvenBlock } from '@/components/audit/ProReport/BreakEvenBlock';
import { FindingCard } from '@/components/audit/ProReport/FindingCard';
import type { Finding } from '@/components/audit/ProReport/types';
import { Button } from '@/components/ui/Button';
import { Link } from '@/i18n/routing';
import type { BreakEven } from '@/lib/audit/break-even';
import styles from './DemoReport.module.css';

// Demo data for the sample report
const DEMO_SITE = {
  url: 'acme-saas.com',
  analyzedAt: '2026-01-06T14:30:00Z',
};

const DEMO_SCORES = {
  overall: 72,
  usability: 68,
  accessibility: 75,
  performance: 78,
  conversion: 65,
};

/** One row of the "top priority fixes" list, ranked on the server. */
export interface DemoPriority {
  title: string;
  auditCode?: string;
  auditCategoryLabel?: string;
  effortEstimate?: Finding['effortEstimate'];
  onActionPath?: boolean;
}

interface DemoReportProps {
  findings: Finding[];
  breakEven: BreakEven;
  priorities: DemoPriority[];
}

type Bucket = 'all' | 'high' | 'medium' | 'low';

/** Critical and serious findings share the "high priority" bucket in the summary. */
function bucketOf(severity: Finding['severity']): Exclude<Bucket, 'all'> {
  if (severity === 'critical' || severity === 'serious' || severity === 'high') return 'high';
  if (severity === 'medium' || severity === 'moderate') return 'medium';
  return 'low';
}

function ScoreGauge({ score, label }: { score: number; label: string }) {
  const getScoreColor = (s: number) => {
    if (s >= 80) return 'var(--low)';
    if (s >= 60) return 'var(--medium)';
    return 'var(--high)';
  };

  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className={styles.scoreGauge}>
      <svg className={styles.gaugeRing} viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke="var(--border)"
          strokeWidth="8"
        />
        <circle
          cx="50"
          cy="50"
          r="40"
          fill="none"
          stroke={getScoreColor(score)}
          strokeWidth="8"
          strokeLinecap="square"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 50 50)"
          className={styles.gaugeProgress}
        />
      </svg>
      <div className={styles.gaugeContent}>
        <span className={styles.gaugeScore} style={{ color: getScoreColor(score) }}>{score}</span>
        <span className={styles.gaugeLabel}>{label}</span>
      </div>
    </div>
  );
}

export function DemoReport({ findings, breakEven, priorities }: DemoReportProps) {
  const t = useTranslations('audit.demoReport');
  const locale = useLocale();
  const fmt = new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(findings.slice(0, 2).map((f, i) => f.id ?? String(i)))
  );
  const [filter, setFilter] = useState<Bucket>('all');

  const counts = {
    total: findings.length,
    high: findings.filter((f) => bucketOf(f.severity) === 'high').length,
    medium: findings.filter((f) => bucketOf(f.severity) === 'medium').length,
    low: findings.filter((f) => bucketOf(f.severity) === 'low').length,
  };

  const filteredFindings = filter === 'all'
    ? findings
    : findings.filter((f) => bucketOf(f.severity) === filter);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className={styles.reportPage}>
      {/* Header */}
      <header className={styles.reportHeader}>
        <div className={styles.headerLeft}>
          <Link href="/" className={styles.backLink}>
            <ArrowLeft size={18} />
            {t('backToHome')}
          </Link>
          <div className={styles.siteInfo}>
            <h1 className={styles.siteUrl}>
              <ExternalLink size={20} />
              {DEMO_SITE.url}
            </h1>
            <span className={styles.analyzedAt}>
              {t('analyzedOn', { date: new Date(DEMO_SITE.analyzedAt).toLocaleDateString() })}
            </span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <a
            href="#screenshot-section"
            className={styles.headerThumbnail}
            title="View analyzed page preview"
          >
            <Monitor size={28} className={styles.headerThumbnailIcon} />
          </a>
          <Button variant="secondary" size="sm">
            <Share2 size={16} />
            {t('share')}
          </Button>
          <Button variant="primary" size="sm">
            <Download size={16} />
            {t('downloadPDF')}
          </Button>
        </div>
      </header>

      {/* Screenshot Section - Placeholder */}
      <section id="screenshot-section" className={styles.screenshotSection}>
        <h2 className={styles.screenshotTitle}>{t('analyzedPage')}</h2>
        <div className={styles.screenshotPlaceholder}>
          <Monitor size={64} className={styles.placeholderIcon} />
          <p className={styles.placeholderText}>
            {t('screenshotPreview', { url: DEMO_SITE.url })}
          </p>
          <p className={styles.placeholderSubtext}>
            {t('runYourOwn')}
          </p>
        </div>
      </section>

      {/* Scores Overview */}
      <section className={styles.scoresSection}>
        <div className={styles.overallScore}>
          <ScoreGauge score={DEMO_SCORES.overall} label={t('overallScore')} />
        </div>
        <div className={styles.categoryScores}>
          <div className={styles.categoryScore}>
            <div className={styles.categoryBar}>
              <div
                className={styles.categoryFill}
                style={{ width: `${DEMO_SCORES.usability}%`, background: 'var(--medium)' }}
              />
            </div>
            <span className={styles.categoryLabel}>{t('usability')}</span>
            <span className={styles.categoryValue}>{fmt.format(DEMO_SCORES.usability)}</span>
          </div>
          <div className={styles.categoryScore}>
            <div className={styles.categoryBar}>
              <div
                className={styles.categoryFill}
                style={{ width: `${DEMO_SCORES.accessibility}%`, background: 'var(--low)' }}
              />
            </div>
            <span className={styles.categoryLabel}>{t('accessibility')}</span>
            <span className={styles.categoryValue}>{fmt.format(DEMO_SCORES.accessibility)}</span>
          </div>
          <div className={styles.categoryScore}>
            <div className={styles.categoryBar}>
              <div
                className={styles.categoryFill}
                style={{ width: `${DEMO_SCORES.performance}%`, background: 'var(--low)' }}
              />
            </div>
            <span className={styles.categoryLabel}>{t('performance')}</span>
            <span className={styles.categoryValue}>{fmt.format(DEMO_SCORES.performance)}</span>
          </div>
          <div className={styles.categoryScore}>
            <div className={styles.categoryBar}>
              <div
                className={styles.categoryFill}
                style={{ width: `${DEMO_SCORES.conversion}%`, background: 'var(--medium)' }}
              />
            </div>
            <span className={styles.categoryLabel}>{t('conversion')}</span>
            <span className={styles.categoryValue}>{fmt.format(DEMO_SCORES.conversion)}</span>
          </div>
        </div>
      </section>

      {/* What is at stake: the same block the paid report and PDF print */}
      <section className={styles.stakeSection}>
        <BreakEvenBlock breakEven={breakEven} locale={locale} />
        <p className={styles.sampleNote}>{t('sampleInputs')}</p>
      </section>

      {/* Summary Stats */}
      <section className={styles.summarySection}>
        <h2 className={styles.sectionTitle}>{t('issuesSummary')}</h2>
        <div className={styles.summaryStats}>
          <button
            className={`${styles.summaryStat} ${filter === 'all' ? styles.active : ''}`}
            onClick={() => setFilter('all')}
          >
            <span className={styles.statNumber}>{fmt.format(counts.total)}</span>
            <span className={styles.statLabel}>{t('totalIssues')}</span>
          </button>
          <button
            className={`${styles.summaryStat} ${styles.high} ${filter === 'high' ? styles.active : ''}`}
            onClick={() => setFilter('high')}
          >
            <span className={styles.statNumber}>{fmt.format(counts.high)}</span>
            <span className={styles.statLabel}>{t('highPriority')}</span>
          </button>
          <button
            className={`${styles.summaryStat} ${styles.medium} ${filter === 'medium' ? styles.active : ''}`}
            onClick={() => setFilter('medium')}
          >
            <span className={styles.statNumber}>{fmt.format(counts.medium)}</span>
            <span className={styles.statLabel}>{t('mediumPriority')}</span>
          </button>
          <button
            className={`${styles.summaryStat} ${styles.low} ${filter === 'low' ? styles.active : ''}`}
            onClick={() => setFilter('low')}
          >
            <span className={styles.statNumber}>{fmt.format(counts.low)}</span>
            <span className={styles.statLabel}>{t('lowPriority')}</span>
          </button>
        </div>
      </section>

      {/* Top priority fixes, ranked the way the PDF ranks them */}
      <section className={styles.prioritiesSection}>
        <h2 className={styles.sectionTitle}>{t('topPriorities')}</h2>
        <p className={styles.prioritiesSub}>{t('topPrioritiesSub')}</p>
        <ol className={styles.prioritiesList}>
          {priorities.map((p, i) => (
            <li key={i} className={styles.priorityItem}>
              <span className={styles.priorityRank}>{String(i + 1).padStart(2, '0')}</span>
              <div className={styles.priorityBody}>
                <div className={styles.priorityHead}>
                  <span className={styles.priorityTitle}>{p.title}</span>
                  <CriterionTag code={p.auditCode} categoryLabel={p.auditCategoryLabel} />
                </div>
                {(p.onActionPath || p.effortEstimate === 'quick') && (
                  <div className={styles.priorityMeta}>
                    {p.onActionPath && <span>{t('onPath')}</span>}
                    {p.effortEstimate === 'quick' && <span>{t('quickFix')}</span>}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Findings, rendered by the same card the paid web report uses */}
      <section className={styles.issuesSection}>
        <h2 className={styles.sectionTitle}>{t('detailedFindings')}</h2>
        <div className={styles.issuesList}>
          {filteredFindings.map((finding, i) => {
            const key = finding.id ?? String(i);
            return (
              <FindingCard
                key={key}
                finding={finding}
                isExpanded={expanded.has(key)}
                onToggle={() => toggle(key)}
              />
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2>{t('ctaTitle')}</h2>
          <p>{t('ctaSubtitle')}</p>
          <div className={styles.ctaButtons}>
            <Link href="/">
              <Button variant="primary" size="lg">
                {t('startAudit')}
              </Button>
            </Link>
            <Link href={{ pathname: '/', hash: 'pricing' }}>
              <Button variant="secondary" size="lg">
                {t('viewPricing')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
